import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Anchor } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const links = [
  { label: "Home", to: "/", hash: "top" },
  { label: "Programs", to: "/", hash: "programs" },
  { label: "About", to: "/", hash: "about" },
  { label: "Parent Information", to: "/", hash: "faq" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const go = (hash) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollTo(hash), 120);
    } else scrollTo(hash);
  };
  const scrollTo = (hash) => {
    if (hash === "top") return window.scrollTo({ top: 0, behavior: "smooth" });
    document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
  };

  const dashPath = user && user.role === "admin" ? "/admin" : "/dashboard";

  return (
    <header className="sticky top-0 z-50 bg-cream/90 backdrop-blur-md border-b border-gold/30">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3" onClick={() => go("top")}>
          <img src="/assets/logo.png" alt="Float Now logo" className="h-14 w-14 object-contain" />
          <span className="hidden sm:block font-serif text-xl font-bold text-navy leading-tight">
            Float Now
            <span className="block text-[10px] tracking-[0.18em] uppercase text-teal font-sans font-semibold">Mermaids &amp; Pirates</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <button key={l.label} data-testid={`nav-${l.hash}`} onClick={() => go(l.hash)}
              className="text-sm font-medium text-navy/80 hover:text-coral transition-colors">
              {l.label}
            </button>
          ))}
          {user ? (
            <>
              <Button asChild variant="ghost" className="text-navy hover:text-coral" data-testid="nav-dashboard">
                <Link to={dashPath}>Dashboard</Link>
              </Button>
              <Button onClick={() => { logout(); navigate("/"); }} variant="outline"
                className="rounded-full border-navy text-navy hover:bg-navy hover:text-cream" data-testid="nav-logout">
                Sign Out
              </Button>
            </>
          ) : (
            <Button asChild variant="outline" className="rounded-full border-navy text-navy hover:bg-navy hover:text-cream" data-testid="nav-signin">
              <Link to="/signin">Sign In</Link>
            </Button>
          )}
          <Button asChild className="rounded-full bg-coral hover:bg-[#C95B50] text-white px-6 shadow-lg shadow-coral/30" data-testid="nav-book">
            <Link to="/book">Book Now</Link>
          </Button>
        </div>

        <button className="lg:hidden text-navy" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-cream border-t border-gold/30 px-6 py-4 flex flex-col gap-3">
          {links.map((l) => (
            <button key={l.label} onClick={() => go(l.hash)} className="text-left py-2 text-navy/80 font-medium" data-testid={`nav-m-${l.hash}`}>
              {l.label}
            </button>
          ))}
          {user ? (
            <>
              <Link to={dashPath} onClick={() => setOpen(false)} className="py-2 text-navy font-medium">Dashboard</Link>
              <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="text-left py-2 text-navy/80 font-medium">Sign Out</button>
            </>
          ) : (
            <Link to="/signin" onClick={() => setOpen(false)} className="py-2 text-navy font-medium">Sign In</Link>
          )}
          <Button asChild className="rounded-full bg-coral text-white mt-2">
            <Link to="/book" onClick={() => setOpen(false)}><Anchor size={16} className="mr-2" />Book Now</Link>
          </Button>
        </div>
      )}
    </header>
  );
}
